import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaEnvelope } from "react-icons/fa6";

export default function EmailLink({
  email,
  subject = "",
  body = "",
  C,
  dark = true,
  disabled = false,
  className = "",
  style = {},
  icon,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) {
  const [isDesktop, setIsDesktop] = useState(true);
  const [open, setOpen] = useState(false);

  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  // Default theme if C is not provided
  const theme = {
    panel: "#111827",
    line: "rgba(255,255,255,0.12)",
    text: "#ffffff",
    ...(C || {}),
  };

  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 224,
  });

  useEffect(() => {
    const check = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    check();

    window.addEventListener("resize", check);

    return () => {
      window.removeEventListener("resize", check);
    };
  }, []);

  const updatePosition = () => {
    if (!wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();

    const MENU_WIDTH = 224;
    const MENU_HEIGHT = isDesktop ? 170 : 60;
    const GAP = 8;
    const PADDING = 16;

    let left = rect.left;

    // Keep menu inside viewport horizontally
    if (left + MENU_WIDTH > window.innerWidth - PADDING) {
      left = window.innerWidth - MENU_WIDTH - PADDING;
    }

    if (left < PADDING) {
      left = PADDING;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top;

    if (spaceBelow >= MENU_HEIGHT) {
      // Open below
      top = rect.bottom + window.scrollY + GAP;
    } else if (spaceAbove >= MENU_HEIGHT) {
      // Open above
      top = rect.top + window.scrollY - MENU_HEIGHT - GAP;
    } else {
      // Open where more space is available
      if (spaceBelow >= spaceAbove) {
        top = rect.bottom + window.scrollY + GAP;
      } else {
        top = Math.max(
          window.scrollY + PADDING,
          rect.top + window.scrollY - MENU_HEIGHT - GAP
        );
      }
    }

    setPosition({
      top,
      left,
      width: MENU_WIDTH,
    });
  };

  useEffect(() => {
    if (!open) return;

    updatePosition();

    const handleResize = () => {
      updatePosition();
    };

    const handleScroll = () => {
      updatePosition();
    };

    const handleClickOutside = (e) => {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, isDesktop]);

  const to = encodeURIComponent(email || "");
  const s = encodeURIComponent(subject);
  const b = encodeURIComponent(body);

  const providers = [
    {
      id: "gmail",
      name: "Gmail",
      icon:
        "https://img.icons8.com/?size=100&id=qyRpAggnV0zH&format=png&color=000000",
      href: `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${s}&body=${b}`,
    },
    {
      id: "outlook",
      name: "Outlook",
      icon:
        "https://img.icons8.com/?size=100&id=ut6gQeo5pNqf&format=png&color=000000",
      href: `https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${s}&body=${b}`,
    },
    {
      id: "zoho",
      name: "Zoho Mail",
      icon:
        "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/zohomail.svg",
      href: `https://mail.zoho.com/zm/#compose?to=${to}&subject=${s}&body=${b}`,
    },
  ];

  const mailtoHref = `mailto:${email}?subject=${s}&body=${b}`;

  if (!email) {
    return null;
  }

  return (
    <>
      <span ref={wrapRef} className="inline-block">
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            if (disabled) return;

            setOpen((current) => !current);

            if (onClick) {
              onClick(e);
            }
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={`inline-flex items-center gap-2 transition ${
            disabled
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "hover:opacity-80"
          } ${className}`}
          style={style}
        >
          {icon}
          {children || email}
        </button>
      </span>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: position.top,
              left: position.left,
              width: position.width,
              background: theme.panel,
              border: `1px solid ${theme.line}`,
              borderRadius: "16px",
              overflow: "hidden",
              zIndex: 999999,
              boxShadow: "0 18px 45px rgba(0,0,0,.25)",
            }}
          >
            {isDesktop ? (
              providers.map((provider) => (
                <a
                  key={provider.id}
                  href={provider.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 18px",
                    color: theme.text,
                    textDecoration: "none",
                    transition: ".25s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = dark
                      ? "rgba(255,255,255,.05)"
                      : "rgba(0,0,0,.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <img
                    src={provider.icon}
                    alt={provider.name}
                    width={22}
                    height={22}
                  />

                  {provider.name}
                </a>
              ))
            ) : (
              <a
                href={mailtoHref}
                onClick={() => setOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  color: theme.text,
                  textDecoration: "none",
                }}
              >
                <FaEnvelope color="#1877F2" />

                Open Mail App
              </a>
            )}
          </div>,
          document.body
        )}
    </>
  );
}